import {HttpErrorResponse} from '@angular/common/http';
import {Injectable} from '@angular/core';
import * as Sentry from '@sentry/angular';
import {Breadcrumb} from '@sentry/angular';
import type {ErrorEvent} from '@sentry/core';
import {Scope} from '@sentry/core';
import {AppConfigService} from './app-config.service';
import {toBusinessOrNull} from './business-error';
import {extractRequestId} from './common-error.handler';

const SENTRY_GROUP_COMMON = 'common';
const SENTRY_ERROR_TAG = 'error_group';
const SENTRY_APP_INITIALIZED_BREADCRUMBS = 'SENTRY_APP_INITIALIZED_STEPS';

@Injectable({
    providedIn: 'root',
})
export class SentryService {

    init(sentryConfig?: SentryConfig): Promise<void> {
        return new Promise((resolve) => {
            AppConfigService.appConfig.sentryEnabled && Sentry.init({
                dsn: AppConfigService.appConfig.sentryDsn,
                tunnel: '/sentry',
                release: AppConfigService.appConfig.frontendVersion,
                environment: AppConfigService.appConfig.sentryEnvironment,
                transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),
                initialScope: (scope: Scope) => this.addTags(scope, sentryConfig?.tagsFactory()),
                beforeBreadcrumb: this.filterBreadcrumbData.bind(this),
                beforeSend: (event: ErrorEvent, _) => this.filterBeforeSend(event, sentryConfig),
            });
            resolve();
        });
    }

    sendErrorMessage(message: string, callback?: (scope: Scope) => void): void {
        Sentry.captureMessage(message, scope => {
            scope.setLevel('error');
            scope.setTag('errorMessage', message);
            callback?.(scope);
            return scope;
        });
    }

    sendMessage(message: string, callback?: (scope: Scope) => void): void {
        Sentry.captureMessage(message, scope => {
            scope.setLevel('info');
            scope.setTag('message', message);
            callback?.(scope);
            return scope;
        });
    }

    addBreadcrumb(breadcrumb: Breadcrumb): void {
        Sentry.addBreadcrumb({
            message: breadcrumb.message,
            data: breadcrumb.data,
            level: breadcrumb.level || 'info',
            category: breadcrumb.category || 'message',
        });
    }

    addTag(key: string, value: any): void {
        Sentry.setTag(key, value);
    }

    setUser(username: string): void {
        Sentry.setUser({username});
    }

    resetUser(): void {
        Sentry.setUser(null);
    }

    private addTags(scope: Scope, sentryTags: Record<string, any> = {}): Scope {
        Object.entries((sentryTags)).forEach(([key, value]) => {
            scope.setTag(key, value);
            scope.setExtra(key, value);
        });
        return scope;
    }

    private filterBreadcrumbData(breadcrumb: Breadcrumb): Breadcrumb {
        if (breadcrumb.category === 'navigation') {
            if (breadcrumb.data.from) {
                breadcrumb.data.from = breadcrumb.data.from.replace(/\?.*/, '');
            }
            if (breadcrumb.data.to) {
                breadcrumb.data.to = breadcrumb.data.to.replace(/\?.*/, '');
            }
        }
        return breadcrumb;
    }

    private filterBeforeSend(event: ErrorEvent, sentryConfig?: SentryConfig): ErrorEvent {
        if (this.matchedByTags(event, sentryConfig?.mutedEventsByTags)) {
            return null;
        }
        Object.entries(sentryConfig?.tagsFactory() || {}).forEach(([key, value]) => {
            event.tags[key] = value;
        });
        const sentryGroup = Object.entries(sentryConfig?.groupsByTags || {}).find(([_, value]) => this.matchedByTags(event, value));
        event.tags[SENTRY_ERROR_TAG] = sentryGroup ? sentryGroup[0] : SENTRY_GROUP_COMMON;
        return event;
    }

    private matchedByTags(event: ErrorEvent, tagsGroup?: Record<string, string>[][]): boolean {
        return tagsGroup?.some(tagGroup =>
            tagGroup.every(tag => Object.entries(tag || {})
                .some(([key, value]) => String(event.tags[key]).includes(value)),
            ),
        );
    }
}

export interface SentryConfig {
    tagsFactory?: () => Record<string, any>;
    mutedEventsByTags?: Record<string, string>[][];
    groupsByTags?: Record<string, Record<string, string>[][]>;
}

export function sendErrorToSentry(error: any): void {
    if (AppConfigService?.appConfig?.sentryEnabled === false) {
        return;
    }
    const message = extractErrorMessage(error);
    if (!Sentry.getClient()) {
        const breadcrumbs = getSentryAppInitializedBreadcrumbs();
        error?.stack && breadcrumbs.push(error.stack);
        sessionStorage.removeItem(SENTRY_APP_INITIALIZED_BREADCRUMBS);
        fetch('/api/frontend/v1/error/log', {
            method: 'POST',
            body: JSON.stringify({
                message: message,
                tags: JSON.stringify({
                    'source': 'sentry.service',
                    'documentReferrer': document?.referrer,
                    'userAgent': window?.navigator?.userAgent,
                    'platform': window?.navigator?.platform,
                    'sessionId': sessionStorage.getItem('preloaderSessionId') || null,
                }),
                breadcrumbs: JSON.stringify(breadcrumbs),
            }),
            headers: {
                'Content-Type': 'application/json',
            },
        });
    } else {
        Sentry.captureException(message, scope => {
            const businessError = toBusinessOrNull(error);
            scope.setTag('httpStatus', error.status);
            scope.setTag('httpStatusText', error.statusText);
            scope.setTag('errorCode', businessError?.error);
            scope.setTag('errorDescription', businessError?.errorDescription);
            scope.setTag('requestId', extractRequestId(error));
            scope.setTag('businessError', !!businessError);
            error.stack && scope.addBreadcrumb(error.stack);
            return scope;
        });
    }
}

function getSentryAppInitializedBreadcrumbs(): string[] {
    return JSON.parse(sessionStorage.getItem(SENTRY_APP_INITIALIZED_BREADCRUMBS)) || [];
}

export function addSentryErrorLog(breadcrumb: string): void {
    const sentryBreadcrumbs = getSentryAppInitializedBreadcrumbs();
    sentryBreadcrumbs.push(breadcrumb);
    sessionStorage.setItem(SENTRY_APP_INITIALIZED_BREADCRUMBS, JSON.stringify(sentryBreadcrumbs));
}

/**
 * Implementation of error extraction for Sentry that handles default error wrapping,
 * HTTP responses, ErrorEvent and few other known cases.
 * https://github.com/getsentry/sentry-javascript/blob/master/packages/angular/src/errorhandler.ts
 */
function extractErrorMessage(errorCandidate: unknown): unknown {
    let error = errorCandidate;
    if (error && (error as {ngOriginalError: Error}).ngOriginalError) {
        error = (error as {ngOriginalError: Error}).ngOriginalError;
    }
    if (error instanceof HttpErrorResponse) {
        if (error.error instanceof Error) {
            error = error.error;
        } else if (error.error instanceof ErrorEvent && error.error.message) {
            error = error.error.message;
        } else if (typeof error.error === 'string') {
            error = `Server returned code ${error.status} with body "${error.error}"`;
        } else {
            error = error.message;
        }
    }
    if (error instanceof Error || error instanceof ErrorEvent) {
        error = error.message;
    }
    return error || 'Handled unknown error';
}
